import { List, Stack } from '@mui/material';
import { compact } from 'lodash';
import { useMemo } from 'react';

import { APPBAR_HEIGHT } from '@/components/architecture/Layout/AppBar/AppBar.constants';
import { GroupedSidebarMenu } from '@/components/architecture/Sidebar/GroupedSidebarMenu';
import { HelpMenuItem } from '@/components/architecture/Sidebar/HelpMenuItem';
import { MenuItemProps } from '@/components/architecture/Sidebar/MenuItem';
import { SidebarWithContainer } from '@/components/architecture/Sidebar/Sidebar';
import { SidebarCollapseToggle } from '@/components/architecture/Sidebar/SidebarCollapseToggle';
import { menuItemSxProps } from '@/components/architecture/Sidebar/sidebarConstants';
import { SidebarFillLayout } from '@/components/architecture/Sidebar/SidebarFillLayout';
import { AuditIcon } from '@/components/icons/AuditIcon';
import { BarChart03Icon } from '@/components/icons/BarChart03Icon';
import { Building07Icon } from '@/components/icons/Building07Icon';
import { CategoriesIcon } from '@/components/icons/CategoriesIcon';
import { ConfigureIcon } from '@/components/icons/ConfigureIcon';
import { DisclaimerIcon } from '@/components/icons/DisclaimerIcon';
import { Flag02Icon } from '@/components/icons/Flag02Icon';
import { FromTemplateIcon } from '@/components/icons/FromTemplateIcon';
import { GraduationHat02Icon } from '@/components/icons/GraduationHat02Icon';
import { IntegrationsIcon } from '@/components/icons/IntegrationsIcon';
import { PaletteIcon } from '@/components/icons/PaletteIcon';
import { PromptsIcon } from '@/components/icons/PromptsIcon';
import { ShieldTickIcon } from '@/components/icons/ShieldTickIcon';
import { SubBrandsIcon } from '@/components/icons/SubBrandsIcon';
import { TeamsIcon } from '@/components/icons/TeamsIcon';
import { TenantsIcon } from '@/components/icons/TenantsIcon';
import { Users01Icon } from '@/components/icons/Users01Icon';
import { UserSettingsIcon } from '@/components/icons/UserSettingsIcon';
import { useSidebarExpansionState } from '@/hooks/useSidebarExpansionState';
import { ROUTE_KEYS } from '@/navigation/constants';
import { useActiveMenuItem } from '@/utils/navigation/activeMenu';

import { useAuthorizedSettingsPages } from './AdminUsersPage/hooks/useAuthorizedAdminPages';

const MENU_ITEMS_ID = 'admin-sidebar-menu-items';
export const ADMIN_SIDEBAR_WIDTH = 167;

type MenuItemPropsWithGroup = MenuItemProps & {
  routeKey?: string;
  activePaths?: readonly string[];
};

type PageConfig = {
  icon: React.ReactElement;
  groupName: string;
  routeKey: string;
  activePaths?: readonly string[];
};

const ADMIN_GROUPS = {
  USER_ADMIN: 'User admin',
  APP_SETTINGS: 'App settings',
  REPORTING: 'Reporting',
  INTERNAL: 'Internal',
} as const;

const PAGE_CONFIGS: Record<string, PageConfig> = {
  Users: {
    icon: <Users01Icon size={16} />,
    groupName: ADMIN_GROUPS.USER_ADMIN,
    routeKey: ROUTE_KEYS.ADMIN_USERS,
  },
  Teams: {
    icon: <TeamsIcon size={16} />,
    groupName: ADMIN_GROUPS.USER_ADMIN,
    routeKey: ROUTE_KEYS.ADMIN_TEAMS,
  },
  Roles: {
    icon: <UserSettingsIcon size={16} />,
    groupName: ADMIN_GROUPS.USER_ADMIN,
    routeKey: ROUTE_KEYS.ADMIN_ROLES,
  },
  'Audit log': {
    icon: <AuditIcon size={16} />,
    groupName: ADMIN_GROUPS.USER_ADMIN,
    routeKey: ROUTE_KEYS.ADMIN_AUDIT_LOG,
  },
  Integrations: {
    icon: <IntegrationsIcon size={16} />,
    groupName: ADMIN_GROUPS.APP_SETTINGS,
    routeKey: ROUTE_KEYS.ADMIN_INTEGRATIONS,
  },
  Security: {
    icon: <ShieldTickIcon size={16} />,
    groupName: ADMIN_GROUPS.APP_SETTINGS,
    routeKey: ROUTE_KEYS.ADMIN_SECURITY,
  },
  Categories: {
    icon: <CategoriesIcon size={16} />,
    groupName: ADMIN_GROUPS.APP_SETTINGS,
    routeKey: ROUTE_KEYS.ADMIN_CATEGORIES,
  },
  Branding: {
    icon: <PaletteIcon size={16} />,
    groupName: ADMIN_GROUPS.APP_SETTINGS,
    routeKey: ROUTE_KEYS.ADMIN_BRANDING,
  },
  'Sub-brands': {
    icon: <SubBrandsIcon size={16} />,
    groupName: ADMIN_GROUPS.APP_SETTINGS,
    routeKey: ROUTE_KEYS.ADMIN_SUB_BRANDS,
  },
  Disclaimers: {
    icon: <DisclaimerIcon size={16} />,
    groupName: ADMIN_GROUPS.APP_SETTINGS,
    routeKey: ROUTE_KEYS.ADMIN_LEGAL_DISCLAIMERS,
  },
  'AI prompts': {
    icon: <PromptsIcon size={16} />,
    groupName: ADMIN_GROUPS.APP_SETTINGS,
    routeKey: ROUTE_KEYS.ADMIN_SUMMARY_PROMPTS,
    activePaths: ['/admin/forms'],
  },
  'Summary prompts': {
    icon: <PromptsIcon size={16} />,
    groupName: ADMIN_GROUPS.APP_SETTINGS,
    routeKey: ROUTE_KEYS.ADMIN_SUMMARY_PROMPTS,
    activePaths: ['/admin/forms'],
  },
  Templates: {
    icon: <FromTemplateIcon size={16} />,
    groupName: ADMIN_GROUPS.APP_SETTINGS,
    routeKey: ROUTE_KEYS.ADMIN_PRESENTATION_TEMPLATES,
  },
  Education: {
    icon: <GraduationHat02Icon size={16} />,
    groupName: ADMIN_GROUPS.APP_SETTINGS,
    routeKey: ROUTE_KEYS.ADMIN_EDUCATION,
  },
  'Document summaries': {
    icon: <BarChart03Icon size={16} />,
    groupName: ADMIN_GROUPS.REPORTING,
    routeKey: ROUTE_KEYS.ADMIN_DOCUMENT_SUMMARIES_REPORT,
  },
  Tenants: {
    icon: <TenantsIcon size={16} />,
    groupName: ADMIN_GROUPS.INTERNAL,
    routeKey: ROUTE_KEYS.ADMIN_TENANTS,
  },
  Organizations: {
    icon: <Building07Icon size={16} />,
    groupName: ADMIN_GROUPS.INTERNAL,
    routeKey: ROUTE_KEYS.ADMIN_ORGANIZATIONS,
  },
  Configurations: {
    icon: <ConfigureIcon size={16} />,
    groupName: ADMIN_GROUPS.INTERNAL,
    routeKey: ROUTE_KEYS.ADMIN_CONFIGURATIONS,
  },
  'Feature flags': {
    icon: <Flag02Icon size={16} />,
    groupName: ADMIN_GROUPS.INTERNAL,
    routeKey: ROUTE_KEYS.ADMIN_FEATURE_FLAGS,
  },
};

function useAdminMenuItems({
  isExpanded,
}: {
  isExpanded: boolean;
}): MenuItemPropsWithGroup[] {
  const authorizedPages = useAuthorizedSettingsPages();

  return useMemo(() => {
    return compact(
      authorizedPages.map((page) => {
        const config = PAGE_CONFIGS[page.display];
        if (!config) return null;

        return {
          text: page.display,
          icon: config.icon,
          to: page.to,
          variant: isExpanded ? 'large' : 'small',
          groupName: config.groupName,
          routeKey: config.routeKey,
          activePaths: config.activePaths,
          showWarningBadge: page.showWarningBadge,
          menuItemSxProps,
        } as MenuItemPropsWithGroup;
      })
    );
  }, [authorizedPages, isExpanded]);
}

export function AdminSidebar() {
  const { expansionState, isExpanded, toggle, isTooltipReady } =
    useSidebarExpansionState();

  const menuItems = useAdminMenuItems({ isExpanded });
  const colorTheme = 'light';

  const { active, prev, fallback } = useActiveMenuItem(menuItems);

  const activeSidebarItem = active ?? prev ?? fallback;

  return (
    <SidebarWithContainer
      expansionState={expansionState}
      data-it="AdminSidebar"
      width={ADMIN_SIDEBAR_WIDTH}
      collapsedWidth={56}
    >
      <Stack
        display="flex"
        direction="column"
        justifyContent="space-between"
        alignItems="center"
        height={`calc(100vh - ${APPBAR_HEIGHT})`}
        overflow="hidden"
      >
        <Stack width="100%" height="100%" pb={1} spacing={2} pt={2}>
          <List sx={{ p: 0, height: '100%' }} id={MENU_ITEMS_ID}>
            <SidebarFillLayout
              isExpanded={isExpanded}
              onCollapseAreaClick={toggle}
              footer={
                <Stack>
                  <SidebarCollapseToggle
                    isExpanded={isExpanded}
                    isTooltipReady={isTooltipReady()}
                    onToggle={toggle}
                    menuItemSxProps={menuItemSxProps}
                    colorTheme={colorTheme}
                  />
                  <HelpMenuItem
                    isExpanded={isExpanded}
                    colorTheme={colorTheme}
                  />
                </Stack>
              }
            >
              <GroupedSidebarMenu
                items={menuItems}
                isExpanded={isExpanded}
                activeTo={activeSidebarItem?.to}
                commonItemProps={{ colorTheme }}
              />
            </SidebarFillLayout>
          </List>
        </Stack>
      </Stack>
    </SidebarWithContainer>
  );
}
